import { NavLink } from "react-router-dom";

import { Seo } from "../../components/Seo/Seo";
import { SocialRow } from "../../components/navigation/SocialRow/SocialRow";
import { routes } from "../../config/routes/routes";
import { ReactComponent as ArrowUpRight } from "../../assets/vectors/icons/arrow-up-right.svg";
import { APPLIA_WEBSITE_URL, REPORT_PDF_URL } from "../../config/env";

import logo from "../../assets/vectors/logo/applia_logo_white.svg";
import coverImage from "../../assets/images/homepage-technical-01.png";
import backgroundImage from "../../assets/images/homepage-washing-machine.jpg";
import pillar1Image from "../../assets/images/content/pillar-1/index/bg-header-2-cropped.webp";
import pillar2Image from "../../assets/images/content/pillar-2/index/bg-header-3.webp";

const DocumentIcon = () => (
	<svg width="20" height="20" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="shrink-0">
		<path d="M10 1H4a1 1 0 00-1 1v14a1 1 0 001 1h10a1 1 0 001-1V7l-5-6z" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
		<path d="M10 1v6h5" stroke="white" strokeWidth="1.5" strokeLinejoin="round" />
	</svg>
);

const ArrowRightIcon = () => (
	<svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true" className="shrink-0">
		<line x1="5.95" y1="12" x2="17.04" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<line x1="12.1" y1="6.57" x2="17.04" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
		<line x1="12.1" y1="17.43" x2="17.04" y2="12" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
	</svg>
);

const cardMeta: Record<string, { image: string | null; startUrl: string }> = {
	"/introduction": { image: null,         startUrl: "/introduction/foreword" },
	"/pillar/1":     { image: pillar1Image, startUrl: "/pillar/1" },
	"/pillar/2":     { image: pillar2Image, startUrl: "/pillar/2" },
};

export const Home = () => {
	return (
		<>
			<Seo
				title="Home"
				description="The APPLiA Home Appliance Monitor 2025 is a deep look into sustainability, digitalisation and European competitiveness as fostered by the home appliance sector."
			/>

			<div
				className="relative min-h-screen flex flex-col isolate"
				style={{
					backgroundColor: "#00348a",
					backgroundImage: `url(${backgroundImage})`,
					backgroundSize: "cover",
					backgroundPosition: "center",
					backgroundRepeat: "no-repeat",
				}}
			>
				{/* Blue gradient overlay on top of the photo */}
				<div
					className="absolute inset-0 -z-10 pointer-events-none"
					style={{
						background:
							"linear-gradient(to right, rgba(0,52,138,0.94) 0%, rgba(0,52,138,0.88) 40%, rgba(42,138,181,0.82) 100%)",
					}}
				/>

				<div className="max-w-[1600px] w-full flex flex-col flex-1">
					{/* Logo */}
					<div className="px-8 lg:px-16 pt-14 pb-2">
						<NavLink to="/">
							<img src={logo} className="w-32" alt="APPLiA logo" />
						</NavLink>
					</div>

					{/* Hero Section */}
					<div className="flex flex-row items-center px-8 lg:px-16 py-10 lg:py-16 gap-10 lg:gap-16 flex-1">
						{/* Left: Text */}
						<div className="flex-1 flex flex-col gap-6">
							<div className="flex flex-col">
								<div className="flex items-center gap-3 mb-4">
									<span className="shrink-0 h-px" style={{ width: "60px", backgroundColor: "#a8c2e8" }} />
									<p
										className="font-normal uppercase tracking-wide"
										style={{ fontSize: "22px", color: "#a8c2e8" }}
									>
										Statistical report
									</p>
								</div>
								<h1
									className="font-bold leading-tight text-white"
									style={{ fontSize: "clamp(2.5rem, 5vw, 4.5rem)", lineHeight: 1.05 }}
								>
									Home Appliance Monitor
								</h1>
								<p
									className="font-light mt-4"
									style={{ fontSize: "56px", color: "#3cb4e5", lineHeight: 1 }}
								>
									2025
								</p>
							</div>
							<p className="text-white text-lg max-w-lg leading-relaxed font-light">
								A deep look into the sustainability, digitalisation and competitiveness of the home appliance sector in Europe
							</p>
							<a
								href={`${window.location.origin}${REPORT_PDF_URL}`}
								className="self-start flex items-center gap-3 px-8 py-4 rounded-lg text-white text-left transition-colors focus:outline-none focus:ring-2 focus:ring-white/40 bg-[rgba(0,71,186,0.4)] hover:bg-[rgba(48,110,205,0.55)]"
								style={{
									border: "1px solid #a8c2e8",
									minWidth: "280px",
								}}
								download="APPLiA - Home Appliance Monitor 2025"
							>
								<DocumentIcon />
								<div className="flex flex-col">
									<span className="text-sm font-medium text-white leading-tight">Download the PDF version</span>
									<span className="text-xs leading-tight" style={{ color: "#99b5e3" }}>PDF • 15,0 MB</span>
								</div>
							</a>
						</div>

						{/* Right: Photo */}
						<div className="flex-1 w-full justify-center lg:justify-end hidden">
							<img
								src={coverImage}
								alt="Home Appliance Monitor cover"
								className="w-full max-w-lg rounded-2xl object-cover"
								style={{ maxHeight: "560px", objectPosition: "center" }}
							/>
						</div>
					</div>

					{/* Pillar Cards */}
					<div className="px-8 lg:px-16 pb-12">
						<div className="grid grid-cols-1 md:grid-cols-[3fr_4fr_4fr] gap-5 items-stretch">
							{routes.map((route) => {
								const meta = cardMeta[route.url ?? ""];
								return (
								<div
									key={route.label}
									className="group relative rounded-xl overflow-hidden flex flex-col transition-colors bg-[#003c9d] hover:bg-[#1a54af] focus-within:ring-2 focus-within:ring-white/60"
									style={{ border: "1px solid #a8c2e8" }}
								>
									{/* Card Image — only for routes that have one */}
									{meta?.image && (
										<div className="relative h-44 overflow-hidden">
											<img
												src={meta.image}
												alt={route.label}
												className="w-full h-full object-cover"
											/>
											<div className="absolute inset-0 pointer-events-none transition-colors bg-[rgba(0,52,138,0.15)] group-hover:bg-transparent" />
										</div>
									)}

									{/* Card Content */}
									<div className="p-5 flex flex-col gap-3 flex-1">
										<div className="flex flex-col gap-1">
											{route.sublabel && <p className="block text-sm uppercase font-light tracking-widest text-white">{route.sublabel}</p>}
											{/* Stretched link: covers the whole card so any click on it opens the pillar,
												    without nesting the bullet links inside another anchor */}
											<NavLink
												to={meta?.startUrl ?? route.url ?? "/"}
												className="font-bold leading-snug hover:text-primary transition-colors focus:outline-none after:content-[''] after:absolute after:inset-0"
												style={{ fontSize: "22px", color: "white" }}
											>
												{route.label}
											</NavLink>
										</div>

										<ul className="space-y-1.5">
											{route.children?.map((child) => {
												const childUrl = (route.url || "") + child.url;
												return (
													<li key={child.label} className="flex items-start gap-2">
														<span className="mt-[0.45rem] w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: "rgba(255,255,255,0.8)" }} />
														<NavLink
															to={childUrl}
															className="relative z-10 text-base font-light leading-snug hover:text-white transition-colors"
															style={{ color: "rgba(255,255,255,0.8)" }}
														>
															{child.label}
														</NavLink>
													</li>
												);
											})}
										</ul>

										<span
											aria-hidden="true"
											className="mt-auto self-end flex items-center gap-2 text-white uppercase font-medium"
											style={{ fontSize: "10pt" }}
										>
											<ArrowRightIcon />
											Start reading
										</span>
									</div>
								</div>
								);
							})}
						</div>
					</div>
				</div>

				{/* Footer — the rule spans the full page width, the content stays in the 1600px column */}
				<div style={{ borderTop: "1px solid rgba(255,255,255,0.2)" }}>
					<div className="max-w-[1600px] w-full px-8 lg:px-16 py-5 flex flex-col sm:flex-row justify-between items-start gap-4">
						<div className="flex flex-col gap-0.5">
							<a
								className="flex items-center gap-1 text-white text-base"
								target="_blank"
								href={APPLIA_WEBSITE_URL}
								rel="noreferrer"
							>
								Visit APPLiA website
								<ArrowUpRight width={20} height={20} color="white" />
							</a>
							<p className="text-sm" style={{ color: "#99b5e3" }}>APPLiA &copy; 2026</p>
						</div>
						<div className="flex flex-col flex-end gap-2">
							<p className="text-white text-sm">Follow us</p>
							<SocialRow />
						</div>
					</div>
				</div>
			</div>
		</>
	);
};
